import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, LockKey, CheckCircle } from "@phosphor-icons/react";

export default function ChangePasswordModal({ open, onClose }) {
  const [form, setForm] = React.useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (open) { setForm({ current: "", next: "", confirm: "" }); setErr(""); setDone(false); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.next.length < 6) { setErr("New password must be at least 6 characters."); return; }
    if (form.next !== form.confirm) { setErr("New password and confirmation don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setDone(true);
      toast.success("Password changed successfully");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white text-gray-900 max-w-sm w-full border border-gray-300" data-testid="change-password-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 flex items-center gap-1.5">
              <LockKey size={14} weight="bold" className="text-[#0020C2]" /> Security
            </div>
            <h3 className="text-lg font-black tracking-tight mt-1">Change my password</h3>
          </div>
          <button onClick={onClose} data-testid="change-password-close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-6 text-center" data-testid="change-password-success">
            <CheckCircle size={32} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-semibold">Your password has been updated.</p>
            <button onClick={onClose} data-testid="change-password-done-close"
              className="mt-4 bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3]">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Current password</label>
              <input type="password" required value={form.current} data-testid="change-password-current"
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">New password</label>
              <input type="password" required value={form.next} data-testid="change-password-new"
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Confirm new password</label>
              <input type="password" required value={form.confirm} data-testid="change-password-confirm"
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
            </div>
            {err && <div className="text-xs text-[#DC2626] border border-[#DC2626] bg-red-50 px-3 py-2" data-testid="change-password-error">{err}</div>}
            <button type="submit" disabled={saving} data-testid="change-password-submit"
              className="w-full bg-[#0020C2] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-50">
              {saving ? "Saving…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
